import type { WsMessage } from "@/api/websocket";
import type { SpaceMessage } from "@/api/spaces";
import type { useConversationStore } from "@/stores/conversation-store";
import { stripCursorDisplayTokens } from "./cursor-token-display";
import { nextPromptFromMetadata } from "./next-prompt-metadata";
import { messageAttachments } from "./message-attachments";
import { nativeToolStepText, toolStepLabel } from "./native-tool-step-text";

/** Native transcripts are durable row snapshots, isolated from Denker's voice/cursor. */
export function applyNativeConversationMessage(
  message: WsMessage,
  store: Pick<typeof useConversationStore, "getState">,
): boolean {
  const data = message.data as Record<string, unknown>;
  const metadata = data.metadata as Record<string, unknown> | undefined;
  if (!metadata?.native_message_id || !metadata.native_subagent_id)
    return false;
  const row = data as unknown as SpaceMessage;
  const rawContent = row.content;
  const content =
    typeof rawContent === "string"
      ? rawContent
      : typeof (rawContent as Record<string, unknown>)?.text === "string"
        ? (rawContent as { text: string }).text
        : JSON.stringify(rawContent);
  const activityKind = metadata.activity_kind;
  store.getState().upsertDurableMessage({
    id: row.id,
    nativeFinal: metadata.native_final === true,
    nativeSubagentId: metadata.native_subagent_id as string,
    role: row.role,
    content: stripCursorDisplayTokens(nativeToolStepText(metadata, content)),
    contentLabel: toolStepLabel(metadata),
    attachments: messageAttachments(rawContent),
    conversationId:
      row.conversation ?? (data.conversation_id as string | undefined),
    executionId: (data.execution_id ??
      metadata.execution_id ??
      row.execution) as string | undefined,
    sessionId: (data.runtime_session_id ?? metadata.runtime_session_id) as
      | string
      | undefined,
    activityKind:
      activityKind === "tool_call" || activityKind === "progress"
        ? activityKind
        : undefined,
    isStreaming: row.role !== "user" && metadata.native_final === false,
    nextPrompt: nextPromptFromMetadata(metadata),
    createdAt: row.created_at ?? message.timestamp ?? new Date().toISOString(),
  });
  return true;
}
